import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ContentCopy as ContentCopyIcon, 
  Email as EmailIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  FileCopy as FileCopyIcon
} from '@mui/icons-material';
import api from '../services/api/apiService';
import { API_URL } from '../services/api/config';
import { useImages } from '../context/images/ImagesContext';

const InvitationPage = () => {
  const { selectedInfluencer } = useImages();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [invitationData, setInvitationData] = useState({
    recipientName: '',
    recipientEmail: '',
    message: '',
    expiryDays: 7,
    includeImages: true
  });
  const [generatedInvitation, setGeneratedInvitation] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [formLinkData, setFormLinkData] = useState({
    expiryDays: 30
  });
  const [generatedFormLink, setGeneratedFormLink] = useState(null);
  const [formLinkLoading, setFormLinkLoading] = useState(false);

  // Refs for scrolling
  const invitationFormRef = useRef(null);
  const formLinkRef = useRef(null);

  // Load existing invitation if available
  useEffect(() => {
    if (selectedInfluencer) {
      loadInvitation();
    } else {
      setGeneratedInvitation(null);
      setGeneratedFormLink(null);
    }
  }, [selectedInfluencer]);

  const loadInvitation = async () => {
    if (!selectedInfluencer) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/invitations/${selectedInfluencer.id}`);
      
      if (response.data) {
        setGeneratedInvitation(response.data);
        
        // Pre-fill form with existing data
        setInvitationData({
          recipientName: response.data.recipientName || '',
          recipientEmail: response.data.recipientEmail || '',
          message: response.data.message || '',
          expiryDays: response.data.expiryDays || 7,
          includeImages: response.data.includeImages !== false
        });
      }
    } catch (error) {
      // It's okay if there's no invitation yet
      if (error.response && error.response.status !== 404) {
        console.error('Error loading invitation:', error);
        setError('Failed to load invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInvitationData({
      ...invitationData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setInvitationData({
      ...invitationData,
      [name]: checked
    });
  };

  const handleGenerateInvitation = async () => {
    if (!selectedInfluencer) {
      setError('Please select an influencer first');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await api.post(`/invitations/${selectedInfluencer.id}`, {
        ...invitationData,
        influencerId: selectedInfluencer.id
      });
      
      setGeneratedInvitation(response.data);
      setSuccess('Invitation generated successfully');
    } catch (error) {
      console.error('Error generating invitation:', error);
      setError('Failed to generate invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!generatedInvitation || !generatedInvitation.invitationLink) return;
    
    navigator.clipboard.writeText(generatedInvitation.invitationLink)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };

  const handleSendEmail = async () => {
    if (!generatedInvitation || !generatedInvitation.id) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await api.post(`/invitations/${generatedInvitation.id}/send`);
      setSuccess('Invitation email sent successfully');
    } catch (error) {
      console.error('Error sending invitation email:', error);
      setError('Failed to send invitation email');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!generatedInvitation || !generatedInvitation.id) return;
    
    try {
      const response = await api.get(`/invitations/${generatedInvitation.id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invitation_${selectedInfluencer.name.toLowerCase().replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF');
    }
  };

  const handleRefreshLink = async () => {
    if (!generatedInvitation || !generatedInvitation.id) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await api.post(`/invitations/${generatedInvitation.id}/refresh`);
      setGeneratedInvitation(response.data);
      setSuccess('Invitation link refreshed successfully');
    } catch (error) {
      console.error('Error refreshing invitation link:', error);
      setError('Failed to refresh invitation link');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFormLinkInputChange = (e) => {
    const { name, value } = e.target;
    setFormLinkData({
      ...formLinkData,
      [name]: value
    });
  };

  const handleGenerateFormLink = async () => {
    if (!selectedInfluencer) {
      setError('Please select an influencer first');
      return;
    }
    
    setFormLinkLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await api.post(
        `/influencers/${selectedInfluencer.id}/generate-hash`, 
        { days_valid: formLinkData.expiryDays }
      );
      
      setGeneratedFormLink(response.data);
      setSuccess('Form link generated successfully');
    } catch (error) {
      console.error('Error generating form link:', error);
      setError('Failed to generate form link');
    } finally {
      setFormLinkLoading(false);
    }
  };

  const handleCopyFormLink = () => {
    if (!generatedFormLink || !generatedFormLink.url) return;
    
    navigator.clipboard.writeText(generatedFormLink.url)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };

  return (
    <Container>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Invitations & Forms
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Create and manage invitations and form links for influencers.
        </Typography>
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="invitation tabs">
          <Tab label="View Results Invitation" />
          <Tab label="Influencer Form Link" />
        </Tabs>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {activeTab === 0 && (
        <div ref={invitationFormRef}>
          {!selectedInfluencer ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Please select an influencer from the sidebar to generate an invitation.
            </Alert>
          ) : loading && !generatedInvitation ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Invitation Details
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Recipient Name"
                    name="recipientName"
                    value={invitationData.recipientName}
                    onChange={handleInputChange}
                    margin="normal"
                    helperText="Name of the person receiving the invitation"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Recipient Email"
                    name="recipientEmail"
                    type="email"
                    value={invitationData.recipientEmail}
                    onChange={handleInputChange}
                    margin="normal"
                    helperText="Email address to send the invitation to"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={4}
                    value={invitationData.message}
                    onChange={handleInputChange}
                    margin="normal"
                    helperText="Custom message to include in the invitation"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="expiry-days-label">Expiry (Days)</InputLabel>
                    <Select
                      labelId="expiry-days-label"
                      name="expiryDays"
                      value={invitationData.expiryDays}
                      onChange={handleInputChange}
                      label="Expiry (Days)"
                    >
                      <MenuItem value={1}>1 day</MenuItem>
                      <MenuItem value={3}>3 days</MenuItem>
                      <MenuItem value={7}>7 days</MenuItem>
                      <MenuItem value={14}>14 days</MenuItem>
                      <MenuItem value={30}>30 days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="include-images-label">Include Images</InputLabel>
                    <Select
                      labelId="include-images-label"
                      name="includeImages"
                      value={invitationData.includeImages}
                      onChange={(e) => setInvitationData({
                        ...invitationData,
                        includeImages: e.target.value
                      })}
                      label="Include Images"
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateInvitation}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Invitation'}
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            {generatedInvitation && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Generated Invitation
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Recipient:
                    </Typography>
                    <Typography variant="body1">
                      {generatedInvitation.recipientName} ({generatedInvitation.recipientEmail})
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Expiry Date:
                    </Typography>
                    <Typography variant="body1">
                      {new Date(generatedInvitation.expiryDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Invitation Link:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        wordBreak: 'break-all', 
                        bgcolor: 'grey.100', 
                        p: 1, 
                        borderRadius: 1 
                      }}
                    >
                      {generatedInvitation.invitationLink}
                    </Typography>
                  </Box>
                  
                  {generatedInvitation.message && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Message:
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {generatedInvitation.message}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={handleRefreshLink}
                    disabled={loading}
                  >
                    Refresh Link
                  </Button>
                  <Button
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyLink}
                    disabled={loading}
                  >
                    Copy Link
                  </Button>
                  <Button
                    startIcon={<EmailIcon />}
                    onClick={handleSendEmail}
                    disabled={loading}
                  >
                    Send Email
                  </Button>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadPDF}
                    disabled={loading}
                  >
                    Download PDF
                  </Button>
                </CardActions>
              </Card>
            )}
          </Grid>
            </Grid>
          )}
        </div>
      )}
      
      {activeTab === 1 && (
        <div ref={formLinkRef}>
          {!selectedInfluencer ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Please select an influencer from the sidebar to generate a form link.
            </Alert>
          ) : (
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Form Link Details
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Generate a secure link for the influencer to access their profile form. 
                        This link contains a hash that verifies the influencer's identity.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="form-expiry-days-label">Expiry (Days)</InputLabel>
                        <Select
                          labelId="form-expiry-days-label"
                          name="expiryDays"
                          value={formLinkData.expiryDays}
                          onChange={handleFormLinkInputChange}
                          label="Expiry (Days)"
                        >
                          <MenuItem value={7}>7 days</MenuItem>
                          <MenuItem value={14}>14 days</MenuItem>
                          <MenuItem value={30}>30 days</MenuItem>
                          <MenuItem value={60}>60 days</MenuItem>
                          <MenuItem value={90}>90 days</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleGenerateFormLink}
                      disabled={formLinkLoading}
                      startIcon={<LinkIcon />}
                    >
                      {formLinkLoading ? <CircularProgress size={24} /> : 'Generate Form Link'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {generatedFormLink && (
                  <Card sx={{ mb: 4 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Generated Form Link
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Influencer:
                        </Typography>
                        <Typography variant="body1">
                          {selectedInfluencer.name}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Expiry Date:
                        </Typography>
                        <Typography variant="body1">
                          {new Date(generatedFormLink.expires_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Form Link:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              wordBreak: 'break-all', 
                              bgcolor: 'grey.100', 
                              p: 1, 
                              borderRadius: 1,
                              flexGrow: 1
                            }}
                          >
                            {generatedFormLink.url}
                          </Typography>
                          <Tooltip title="Copy link">
                            <IconButton 
                              onClick={handleCopyFormLink}
                              sx={{ ml: 1 }}
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Share this link with the influencer to allow them to fill out their profile form.
                        The link will expire on {new Date(generatedFormLink.expires_at).toLocaleDateString()}.
                      </Alert>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                      <Button
                        startIcon={<RefreshIcon />}
                        onClick={handleGenerateFormLink}
                        disabled={formLinkLoading}
                      >
                        Generate New Link
                      </Button>
                      <Button
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyFormLink}
                        disabled={formLinkLoading}
                      >
                        Copy Link
                      </Button>
                    </CardActions>
                  </Card>
                )}
              </Grid>
            </Grid>
          )}
        </div>
      )}
      
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Link copied to clipboard"
      />
    </Container>
  );
};

export default InvitationPage;
